import React from "react";
import { HASHTAG_MAX_LENGTH, removeWhiteSpace } from "@/utils";
import { Badge } from "@/components/ui";
import { XIcon } from "lucide-react";

const MAX_HASHTAGS = 30;
interface Props {
  tags: any[];
  unavailableTags: string[];

  placeholder?: string;
  dataElmId: string;
  tagDeleteIcon?: string;
  addTagOnEnterKeyPressed?: boolean;
  uniquenessTags?: boolean;

  tagDeleteStyle?: string;
  tagStyle?: string;
  inputStyle?: string;
  wrapperStyle?: string;

  onTagsChanged: (tags: any[]) => void;
  onInputChanged?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputRepeatedError: (tagsName: string[]) => void;
}

export const HashTag: React.FC<Props> = ({
  tags,
  unavailableTags,
  uniquenessTags = true,
  dataElmId,
  placeholder = "Type some Tags...",
  addTagOnEnterKeyPressed = true,
  onTagsChanged,
  onInputChanged,
  onInputRepeatedError,
}) => {
  const [selectedTags, setSelectedTags] = React.useState(tags);

  const input = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    setSelectedTags(() => tags);
  }, [tags]);

  React.useEffect(() => {
    clearInput();
    onTagsChanged(selectedTags);
  }, [selectedTags]);

  const addTags = (newTags: string[]) => {
    setSelectedTags(selected => {
      let i = selected.length + 1;
      return selected.concat(
        newTags.map(newTag => {
          return {
            index: i++,
            displayValue: removeWhiteSpace(newTag).replace(/#/g, ""),
          };
        })
      );
    });
  };

  const onInputKeyUp = (e: any): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      const inputValue = e.target.value || "";
      if (addTagOnEnterKeyPressed) {
        onNewInput(inputValue);
      }
    }
  };

  const onNewInput = (newInputValue: string): void => {
    if (newInputValue.length > 0) {
      const inputValues = newInputValue.split(/[ ,]/);

      let errors: string[] = [];
      if (selectedTags.length + inputValues.length > MAX_HASHTAGS) {
        errors = ["You can add a maximum of 30 hashtags"];
      } else {
        const tagsToAdd: string[] = [];
        inputValues.forEach(value => {
          const inputValue = removeWhiteSpace(value).replace(/#/g, "");
          const notAddedSoFar = tagsToAdd.indexOf(inputValue) === -1;
          const inputNotEmpty = inputValue !== "";
          if (inputNotEmpty && addTagOnEnterKeyPressed && notAddedSoFar) {
            const hashtagError = getHashtagError(inputValue);
            if (!hashtagError) {
              tagsToAdd.push(inputValue);
            } else if (errors.indexOf(hashtagError) === -1) {
              errors.push(hashtagError);
            }
          }
        });
        if (tagsToAdd.length > 0) {
          addTags(tagsToAdd);
        }
      }
      if (errors.length > 0) {
        onInputRepeatedError(errors);
      }
    }
  };

  const inputWillChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onInputChanged) {
      onInputChanged(e);
    }
  };

  const onInputKeyDown = (e: any): void => {
    const deleteLastTag = () => {
      setSelectedTags(oldTags => {
        return [...oldTags.splice(0, oldTags.length - 1)];
      });
    };
    if (e.key === "Enter") {
      e.preventDefault();
    }
    if (e.key === "Backspace" && e.target.selectionStart === 0) {
      deleteLastTag();
    } else if (e.key !== "Backspace" && e.target.value.length >= HASHTAG_MAX_LENGTH) {
      e.preventDefault();
    }
  };

  const getHashtagError = (newValue: string): string | null => {
    if (!uniquenessTags) {
      return null;
    }
    const currentHastTags = selectedTags.map(a => a.displayValue.toLowerCase());
    const valueToCompare = newValue.toLowerCase().replace(/#/g, "");
    if (currentHastTags.includes(valueToCompare)) {
      return `The hashtag '${newValue}' has already been used.`;
    } else if (unavailableTags.includes(valueToCompare)) {
      return `The hashtag '${newValue}' is not allowed.`;
    }
    return null;
  };

  const clearInput = (): void => {
    input!.current!.value = "";
  };
  const focusInput = (): void => {
    input!.current!.focus();
  };

  const removeTag = (index: number): void => {
    setSelectedTags(oldTags => {
      return [...oldTags.filter(tag => tag.index !== index)];
    });
  };

  const renderTags = (): any => {
    return selectedTags.length === 0 ? null : (
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tag, index) => (
          <Badge key={index} variant="outline" className={"text-sm"}>
            {tag && tag.displayValue}
            <XIcon size={13} onClick={() => removeTag(tag.index)} className="ml-2 cursor-pointer" />
          </Badge>
        ))}
      </div>
    );
  };

  const renderPlaceholder = (): string | undefined => {
    return selectedTags && selectedTags.length > 0 ? undefined : placeholder;
  };

  return (
    <div
      onClick={focusInput}
      className="flex flex-col gap-2 p-2.5 bg-white border border-slate-200 rounded-lg min-h-[38px] max-h-[111px] overflow-y-auto w-full text-left"
    >
      {renderTags()}
      <input
        ref={input}
        data-elm-id={dataElmId}
        onChange={inputWillChange}
        placeholder={renderPlaceholder()}
        type="text"
        onKeyUp={onInputKeyUp}
        onKeyDown={onInputKeyDown}
        onPaste={text => {
          onNewInput(text.clipboardData.getData("Text"));
          text.preventDefault();
        }}
        className="noBorderInput text-sm bg-white border-none rounded-[3px] outline-none inline-block w-full text-[#474c58] font-normal placeholder:font-light placeholder:italic placeholder:text-[#474c58]/80 py-1"
      />
    </div>
  );
};
